import {
  EpisodeSubType,
  StandaloneChapterSubType,
  StationSubType,
} from '@audacy-clients/client-services/core';
import { type INowPlayingState, isAdState } from '@audacy-clients/core/atoms/player';
import { formatFromTo, formatPublishDate } from '@audacy-clients/core/utils/date';
import { secondsToMinutes } from 'date-fns';
import i18n from 'i18next';
import { isUndefined } from 'lodash';
import { useRecoilValue } from 'recoil';

type TPlayerMetadataProps = INowPlayingState & {
  artist?: string;
  category?: string;
  contentType?: string;
  endTime?: number;
  isBroadcastShow: boolean;
  isLive?: boolean;
  publishDate?: Date;
  startTime?: number;
  station?: string;
  title?: string;
};

type TPlayerMetadata = {
  miniPlayerSubtitle: string;
  miniPlayerTitle: string;
  playerSubtitle: string;
  playerTitle: string;
};

const getPlayerMetadataConfig = (playerItem?: TPlayerMetadataProps) => {
  const common = {
    artist: playerItem?.artist,
    title: playerItem?.title,
    station: playerItem?.station,
    startTime: playerItem?.startTime,
    endTime: playerItem?.endTime,
    category: playerItem?.dataObject?.category,
    contentType: playerItem?.dataObject?.subtype,
    publishDate: playerItem?.dataObject?.publishDate,
    isLive: playerItem?.isLive,
    isBroadcastShow: false,
  };

  switch (playerItem?.dataObject?.subtype) {
    case StationSubType.BROADCAST: {
      return {
        ...common,
        publishDate: playerItem?.episodeDataObject?.publishDate,
        isBroadcastShow:
          playerItem?.episodeDataObject?.subtype === EpisodeSubType.BROADCAST_SHOW_EPISODE,
      };
    }
    case StandaloneChapterSubType.BROADCAST_EPISODE_CHAPTER: {
      const duration = playerItem?.duration ? ` · ${secondsToMinutes(playerItem?.duration)}m` : '';
      return {
        ...common,
        title: `${common.title} ${duration}`,
      };
    }

    default: {
      return common;
    }
  }
};

const usePlayerMetadata = (props?: TPlayerMetadataProps): TPlayerMetadata => {
  const {
    artist = '',
    category = '',
    contentType,
    endTime,
    isBroadcastShow = false,
    publishDate,
    startTime,
    station = '',
    title = '',
  } = getPlayerMetadataConfig(props);

  const getSubTitle = (artist: string, station: string, isValidArtist: boolean, isValidStation: boolean) => {
    if (isValidArtist && isValidStation) {
      return `${artist} • ${station}`
    } else if (isValidArtist && !isValidStation) {
      return`${artist}`
    } else {
     return `${station}`
    }
  }

  const isAd = useRecoilValue(isAdState);

  const isValidArtist = !!artist && artist !== title;
  let isValidArtistForSubTitle = isValidArtist
  if (contentType == StationSubType.EXCLUSIVE || (contentType == StationSubType.BROADCAST && category === 'Music')) {
    isValidArtistForSubTitle = false;
  }
  const isValidStation = artist !== station && station.length > 0;

  let playerTitle = title;
  let playerSubtitle = getSubTitle(artist, station, isValidArtistForSubTitle, isValidStation)
  let miniPlayerTitle = title;
  let miniPlayerSubtitle = getSubTitle(artist, station, isValidArtist, isValidStation)

  const formattedPublishDate = publishDate ? ` • ${formatPublishDate(publishDate)}` : '';

  switch (contentType) {
    case StationSubType.EXCLUSIVE: {
      playerTitle += isValidArtist ? ` • ${artist}` : '';
      break;
    }
    case StationSubType.PODCAST: {
      playerSubtitle = artist + formattedPublishDate;
      miniPlayerSubtitle = artist;
      break;
    }
    case StationSubType.BROADCAST: {
      if (isBroadcastShow && !isUndefined(startTime) && !isUndefined(endTime)) {
        playerSubtitle += ` •  ${formatFromTo(startTime, endTime)}`;
      }
      if (category === 'Music') {
        playerTitle += isValidArtist ? ` • ${artist}` : '';
      }
      break;
    }
    case StandaloneChapterSubType.BROADCAST_EPISODE_CHAPTER: {
      playerSubtitle = station + formattedPublishDate;
      miniPlayerSubtitle = station;
      break;
    }
  }

  if (isAd) {
    miniPlayerTitle = i18n.t('global.ad');
    playerSubtitle = `${station} • ${i18n.t('global.ad')}`;
  }

  return {
    miniPlayerSubtitle,
    miniPlayerTitle,
    playerSubtitle,
    playerTitle,
  };
};

export default usePlayerMetadata;
